import AuthService from '../services/AuthService';
import { HttpMethod, userNameObject, userProfileObject } from "./types";
import { userColorArray } from "../constants/constants";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

const stringToNumericalValue = (str: string): number => {
    return Array.from(str).reduce((acc, _, i) => {
        return acc + str.charCodeAt(i);
    }, 0);
}

export const getInitials = (user: userNameObject) => {
    if (user.family_name && user.given_name) {
        return user.given_name.charAt(0).toUpperCase() + user.family_name.charAt(0).toUpperCase()
    }
    else {
        return user.preferred_username.charAt(0).toUpperCase()
    }
}

export const getProfileObject = (user: userNameObject): userProfileObject => {
    const initials = getInitials(user);
    const cIndex = stringToNumericalValue(user.preferred_username);
    const cObj = userColorArray[cIndex % userColorArray.length];

    return {
        initials: initials,
        bgColor: cObj.bg,
        txtColor: cObj.txt
    }
}

export function parseJwt(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

// Add status translation and icon mapping here
export const statusTranslation: { [key: number]: string } = {
    0: 'Skickat',
    1: 'Öppnat',
    2: 'Godkänt',
    3: 'Avvikelse',
    4: 'Fått fakturor',
    5: 'Återöppnat' // New status for "Re-opened"
};

export const statusIconColorMap: { [key: number]: { label: string; color: string; icon: any } } = {
    0: { label: 'Skickat', color: '#F5A32D', icon: faExclamationCircle },   // Yellow
    1: { label: 'Öppnat', color: '#F5A32D', icon: faExclamationCircle },    // Yellow
    2: { label: 'Godkänt', color: '#07A235', icon: faCheckCircle },         // Green
    3: { label: 'Avvikelse', color: '#DC143C', icon: faTimesCircle },       // Red
    4: { label: 'Fått fakturor', color: '#DC143C', icon: faTimesCircle },   // Red
    5: { label: 'Återöppnat', color: '#F5A32D', icon: faExclamationCircle }       // Green for re-opened
};

async function noAuthBaseFetch(url: string, method: HttpMethod = 'GET', body: any = {}, headers: any = {}, redo: boolean = false): Promise<Response> {
    return fetch(url, {
        method: method,
        body: method != 'GET' ? body : undefined,
        headers: headers
    })
        .then(async response => {
            if (!response.ok)
                throw response;
            return response;
        })
        .catch((e) => {
            throw e;
        });
}

async function baseFetch(url: string, method: HttpMethod = 'GET', body: any = {}, headers: any = {}, redo: boolean = false): Promise<Response> {
    await AuthService.ensureToken();
    let jwt = AuthService.getToken();
    return fetch(url, {
        method: method,
        body: method != 'GET' ? body : undefined,
        headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
            ...headers
        }
    })
        .then(async response => {
            if (!response.ok)
                throw response;
            return response;
        })
        .catch((e) => {
            throw e;
        });
}

export const noAuthApiFetch = (url: string, method: HttpMethod = 'GET', body: any = {}, headers: any = {}): Promise<Response> =>
    noAuthBaseFetch(AppSettings.APIprefix + url, method, body, { ...headers });

export const apiFetch = (url: string, method: HttpMethod = 'GET', body: any = {}, headers: any = {}): Promise<Response> =>
    baseFetch(AppSettings.APIprefix + url, method, body, { ...headers });

export const kcFetch = (url: string, method: HttpMethod = 'GET', body: any = "", headers: any = {}): Promise<Response> =>
    baseFetch(AppSettings.KCprefix + url, method, body, headers);

export enum Code {
    OK = 0,
    ADD_ERR = 1,
    UPDATE_ERR = 2,
    DUPLICATE_ERR = 3,
    DELETE_ERR = 4,
    GET_ERR = 5,
    NOT_FOUND = 6
}

export type FetchReturn<T> = {
    Content: T;
    Message: string;
    Code: Code
}

export function convertToQuerystring(opt: any) {
    return Object.keys(opt ?? {}).reduce((prev, currKey, i, self) => {
        //@ts-ignore
        const potentialValue = opt[currKey];
        if (potentialValue) {
            return prev.concat(`${currKey}=${potentialValue}`);
        }
        return prev;
    }, [] as string[]).join("&");
}

export function ReadFriendlyNumber(val: number): string {
    return val.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$& ').replace(".", ",")
}
